
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getCompanyBankAccounts, createCompanyBankAccount, getCompanyAchSettings, deleteCompanyBankAccount } from "services/company";
import PlaidLinkButton from "components/PlaidLinkButton";
import ConfirmModal from "components/ConfirmModal";

const EMPTY_FORM = {
    accountNumber: "",
    routingNumber: "",
    institutionName: "",
    subtype: "checking",
};

const WithdrawalBankAccountSection = () => {
    const queryClient = useQueryClient();
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [error, setError] = useState("");
    const [deleteError, setDeleteError] = useState("");
    const [plaidSuccess, setPlaidSuccess] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null); // holds account id to delete
    const companyId = localStorage.getItem("companyId") || "";

    const { data: achSettings } = useQuery({ queryKey: ["company-ach-settings"], queryFn: getCompanyAchSettings });
    const isPlaidConnected = plaidSuccess || (() => {
        if (!achSettings) return false;
        if (typeof achSettings === "string") {
            try { const p = JSON.parse(achSettings); return !!(p?.dwollaFundingSourceUrl || p?.settings?.dwollaFundingSourceUrl); } catch { return achSettings.includes("dwollaFundingSourceUrl"); }
        }
        // API returns { companyId, settings: { dwollaFundingSourceUrl, ... } }
        return !!(achSettings?.settings?.dwollaFundingSourceUrl || achSettings?.dwollaFundingSourceUrl);
    })();

    const deleteMutation = useMutation({
        mutationFn: deleteCompanyBankAccount,
        onSuccess: () => {
            setDeleteError("");
            queryClient.invalidateQueries({ queryKey: ["company-bank-accounts"] });
        },
        onError: (err) => {
            const msg = err?.response?.data?.message
                ?? err?.response?.data?.title
                ?? err?.response?.data
                ?? err?.message
                ?? "Failed to delete bank account.";
            const status = err?.response?.status ? ` (${err.response.status})` : "";
            setDeleteError(typeof msg === "string" ? msg + status : `Error${status} — check console for details`);
            console.error("Delete bank account error:", err?.response ?? err);
        },
    });

    const { data: rawAccounts, isLoading } = useQuery({
        queryKey: ["company-bank-accounts"],
        queryFn: getCompanyBankAccounts,
    });

    const accounts = Array.isArray(rawAccounts)
        ? rawAccounts
        : (rawAccounts?.data ?? rawAccounts?.items ?? []);

    const addMutation = useMutation({
        mutationFn: createCompanyBankAccount,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["company-bank-accounts"] });
            setForm(EMPTY_FORM);
            setShowForm(false);
            setError("");
        },
        onError: (err) => {
            setError(err?.response?.data?.message ?? err?.message ?? "Failed to add bank account.");
        },
    });

    const handleChange = (e) => {
        setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setError("");
        if (!form.accountNumber || !form.routingNumber || !form.institutionName) {
            setError("Account number, routing number, and institution name are required.");
            return;
        }
        addMutation.mutate(form);
    };

    return (
        <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
                <h2 className="sub-title" style={{ margin: 0 }}>Withdrawal Bank Account</h2>
                {!showForm && !isPlaidConnected && (
                    <button className="btn-primary" onClick={() => setShowForm(true)}>
                        + Add Bank Account
                    </button>
                )}
            </div>

            {/* Plaid connection */}
            {!isPlaidConnected && (
                <div style={{ background: "#f8f9fa", border: "1px solid #e0e0e0", borderRadius: 8, padding: 20, marginBottom: 20 }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
                        <div style={{ flex: 1, minWidth: 220 }}>
                            <p style={{ fontWeight: 700, fontSize: 14, marginBottom: 4, color: "#111827" }}>Connect via Plaid (Recommended)</p>
                            <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>
                                Securely link your business bank account through Plaid. This creates a verified Dwolla funding source for instant payroll disbursements.
                            </p>
                        </div>
                        <div style={{ paddingTop: 2 }}>
                            <PlaidLinkButton
                                userId={companyId}
                                ownerType="company"
                                companyId={companyId}
                                label="Connect Bank Account"
                                onSuccess={() => {
                                    setPlaidSuccess(true);
                                    queryClient.invalidateQueries({ queryKey: ["company-bank-accounts"] });
                                }}
                                onError={(msg) => setError(msg)}
                            />
                        </div>
                    </div>
                    <div style={{ marginTop: 16, borderTop: "1px solid #e0e0e0", paddingTop: 12 }}>
                        <button
                            type="button"
                            style={{ background: "none", border: "none", color: "#6b7280", fontSize: 12, cursor: "pointer", padding: 0, textDecoration: "underline" }}
                            onClick={() => setShowForm(f => !f)}
                        >
                            {showForm ? "Hide manual entry" : "Enter account details manually instead"}
                        </button>
                    </div>
                </div>
            )}

            {isPlaidConnected && (
                <div style={{ background: "#f0fdf4", border: "1.5px solid #86efac", borderRadius: 8, padding: "14px 18px", marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                        <div>
                            <p style={{ margin: 0, fontWeight: 700, color: "#166534", fontSize: 13 }}>Bank account connected via Plaid</p>
                            <p style={{ margin: 0, fontSize: 12, color: "#166534" }}>Your funding source is verified and ready for payroll disbursements.</p>
                        </div>
                    </div>
                    <PlaidLinkButton
                        userId={companyId}
                        ownerType="company"
                        companyId={companyId}
                        label="Re-connect"
                        onSuccess={() => { setPlaidSuccess(true); queryClient.invalidateQueries({ queryKey: ["company-ach-settings"] }); }}
                        onError={(msg) => setError(msg)}
                    />
                </div>
            )}

            {showForm && (
                <form onSubmit={handleSubmit} style={{ background: "#f8f9fa", border: "1px solid #e0e0e0", borderRadius: 8, padding: 24, marginBottom: 24 }}>
                    <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 16 }}>Add Bank Account</h3>

                    {error && (
                        <div style={{ background: "#ff4d4f22", border: "1px solid #ff4d4f", borderRadius: 6, padding: "10px 14px", marginBottom: 16, color: "#ff4d4f", fontSize: 14 }}>
                            {error}
                        </div>
                    )}

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                        <div>
                            <label style={{ display: "block", marginBottom: 6, fontSize: 13, color: "#374151" }}>Institution Name *</label>
                            <input
                                className="input-field"
                                name="institutionName"
                                value={form.institutionName}
                                onChange={handleChange}
                                placeholder="e.g. Chase Bank"
                            />
                        </div>
                        <div>
                            <label style={{ display: "block", marginBottom: 6, fontSize: 13, color: "#374151" }}>Account Type</label>
                            <select className="input-field" name="subtype" value={form.subtype} onChange={handleChange}>
                                <option value="checking">Checking</option>
                                <option value="savings">Savings</option>
                            </select>
                        </div>
                        <div>
                            <label style={{ display: "block", marginBottom: 6, fontSize: 13, color: "#374151" }}>Account Number *</label>
                            <input
                                className="input-field"
                                name="accountNumber"
                                value={form.accountNumber}
                                onChange={handleChange}
                                placeholder="Account number"
                            />
                        </div>
                        <div>
                            <label style={{ display: "block", marginBottom: 6, fontSize: 13, color: "#374151" }}>Routing Number *</label>
                            <input
                                className="input-field"
                                name="routingNumber"
                                value={form.routingNumber}
                                onChange={handleChange}
                                placeholder="9-digit routing number"
                                maxLength={9}
                            />
                        </div>
                    </div>

                    <div style={{ display: "flex", gap: 10 }}>
                        <button type="submit" className="btn-primary" disabled={addMutation.isPending}>
                            {addMutation.isPending ? "Saving..." : "Save Bank Account"}
                        </button>
                        <button type="button" className="btn-secondary" onClick={() => { setShowForm(false); setForm(EMPTY_FORM); setError(""); }}>
                            Cancel
                        </button>
                    </div>
                </form>
            )}

            {deleteError && (
                <div style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 8, padding: "12px 16px", marginBottom: 16, color: "#dc2626", fontSize: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>{deleteError}</span>
                    <button onClick={() => setDeleteError("")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#dc2626", lineHeight: 1 }}>×</button>
                </div>
            )}

            {isLoading ? (
                <p style={{ color: "var(--text-muted, #aaa)" }}>Loading bank accounts...</p>
            ) : accounts.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "var(--text-muted, #aaa)" }}>
                    <p>No withdrawal bank accounts on file.</p>
                    <p style={{ fontSize: 13 }}>Add a bank account to enable direct payroll liability withdrawals.</p>
                </div>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {accounts.map((acct, idx) => (
                        <div key={acct.id ?? idx} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                            <div>
                                <div style={{ fontWeight: 600, marginBottom: 4, color: "#111827" }}>{acct.institutionName ?? "Bank Account"}</div>
                                <div style={{ fontSize: 13, color: "#6b7280" }}>
                                    Account: ••••{String(acct.accountNumber ?? "").slice(-4)}
                                    &nbsp;&nbsp;|&nbsp;&nbsp;
                                    Routing: {acct.routingNumber}
                                    &nbsp;&nbsp;|&nbsp;&nbsp;
                                    {(acct.subtype ?? "checking").charAt(0).toUpperCase() + (acct.subtype ?? "checking").slice(1)}
                                </div>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                                {acct.isVerified ? (
                                    <span style={{ background: "#16a34a", color: "#fff", borderRadius: 20, padding: "4px 14px", fontSize: 12, fontWeight: 700 }}>
                                        Plaid Verified
                                    </span>
                                ) : (
                                    <>
                                        <span style={{ background: "#f59e0b", color: "#1a1a1a", borderRadius: 20, padding: "4px 14px", fontSize: 12, fontWeight: 700 }}>
                                            Unverified
                                        </span>
                                        <PlaidLinkButton
                                            userId={companyId}
                                            bankAccountId={acct.id}
                                            ownerType="company"
                                            companyId={companyId}
                                            label="Verify with Plaid"
                                            onSuccess={() => queryClient.invalidateQueries({ queryKey: ["company-bank-accounts"] })}
                                            onError={(msg) => setError(msg)}
                                        />
                                    </>
                                )}
                                <button
                                    type="button"
                                    onClick={() => setConfirmDelete(acct.id)}
                                    disabled={deleteMutation.isPending}
                                    style={{ background: "none", border: "1px solid #dc2626", color: "#dc2626", borderRadius: 6, padding: "4px 12px", fontSize: 12, cursor: "pointer", fontWeight: 600 }}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ConfirmModal
                show={!!confirmDelete}
                title="Delete Bank Account?"
                message="This bank account will be permanently removed. This action cannot be undone."
                confirmText="Delete"
                onConfirm={() => { deleteMutation.mutate(confirmDelete); setConfirmDelete(null); }}
                onCancel={() => setConfirmDelete(null)}
            />
        </div>
    );
};

export default WithdrawalBankAccountSection;
